import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/** Swipeable / clickable image carousel for a listing gallery. */
export function ImageCarousel({ images, alt }: { images: string[]; alt: string }) {
  const [index, setIndex] = useState(0);
  const total = images.length;
  const go = (delta: number) => setIndex((i) => (i + delta + total) % total);

  return (
    <div className="relative overflow-hidden rounded-3xl bg-secondary">
      <img
        loading="eager"
        decoding="async"
        src={images[index]}
        alt={`${alt} — image ${index + 1} of ${total}`}
        className="h-56 w-full object-cover sm:h-72 lg:h-[420px]"
      />
      {total > 1 && (
        <>
          <button
            type="button"
            onClick={() => go(-1)}
            aria-label="Previous image"
            className="absolute left-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-background/90 text-brown-dark shadow-soft transition hover:bg-background"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={() => go(1)}
            aria-label="Next image"
            className="absolute right-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-background/90 text-brown-dark shadow-soft transition hover:bg-background"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
          <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-1.5">
            {images.map((src, i) => (
              <button
                key={`${src}-${i}`}
                type="button"
                onClick={() => setIndex(i)}
                aria-label={`Go to image ${i + 1}`}
                aria-current={i === index}
                className={`h-1.5 rounded-full transition-all ${
                  i === index ? "w-5 bg-background" : "w-1.5 bg-background/60"
                }`}
              />
            ))}
          </div>
          <span className="absolute right-3 top-3 rounded-full bg-background/90 px-2.5 py-1 text-[11px] font-semibold text-brown-dark shadow-soft">
            {index + 1} / {total}
          </span>
        </>
      )}
    </div>
  );
}
